import React, { useState, useRef, useCallback } from 'react';
import './cards.css';

const RareAutoCard = ({ player, scale = 1, interactive = true, showBack = false }) => {
  const [flipped, setFlipped] = useState(false);
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    el.style.transform = `perspective(800px) rotateY(${(x - 0.5) * 12}deg)`;
    el.style.setProperty('--lx', `${50 + (0.5 - x) * 12}%`);
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (el) el.style.transform = 'perspective(800px) rotateY(0deg)';
    if (el) { el.style.setProperty('--lx', '50%'); el.style.setProperty('--ly', '20%'); }
  }, []);

  const {
    name, team, teamFull, position, number, cityColor,
    playerId, teamLogoId, sport = 'mlb',
    teamColor, nameplateColor,
    photoUrl, photoPosition = 'center 15%',
    fontSize = 18,
    bio = {},
    stats = [],
    statHeaders = [],
    highlights = [],
    cardNumber = '',
    numbering = '17/25',
    autoPath = 'M6,44 C10,16 18,8 22,18 C26,28 14,38 10,32 C8,26 16,18 26,24 C34,30 28,44 36,36 C42,28 38,18 48,22 C56,26 52,38 60,32 C66,26 70,36 78,28 C84,20 90,34 98,24 C104,16 112,30 120,22',
    autoColor = 'rgba(40,80,220,1)',
    autoImage,
  } = player;

  const headshotUrl = sport === 'nhl'
    ? `https://assets.nhle.com/mugs/nhl/latest/${playerId}.png`
    : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${playerId}/headshot/silo/current`;

  const actionUrl = photoUrl || (sport === 'nhl'
    ? `https://assets.nhle.com/mugs/actionshots/1296x729/${playerId}.jpg`
    : `./cards/photos/players/${playerId}.jpg`);

  const teamLogoUrl = sport === 'nhl'
    ? `https://assets.nhle.com/logos/nhl/svg/${team}_light.svg`
    : `https://www.mlbstatic.com/team-logos/${teamLogoId || playerId}.svg`;

  return (
    <div className="bdc-card-outer" ref={interactive ? cardRef : undefined} onMouseMove={interactive ? handleMouseMove : undefined} onMouseLeave={interactive ? handleMouseLeave : undefined} style={{ transition: interactive ? 'transform 0.25s ease-out' : 'none', ...(scale !== 1 ? { width: `${320 * scale}px`, height: `${450 * scale}px`, overflow: 'hidden' } : {}) }}>
      <div className="bdc-card-scaler" style={scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top left' } : undefined}>
      <div className={`bdc-card-flipper ${flipped ? 'flipped' : ''}`} onClick={interactive ? () => setFlipped(!flipped) : undefined}>

        {/* FRONT */}
        <div className="bdc-card-face bdc-card-front">
          <div className="bdc-card-bg" style={{ background: `linear-gradient(160deg, ${teamColor} 0%, ${teamColor}cc 40%, ${teamColor}99 100%)` }} />
          {interactive && <div className="bdc-shimmer" />}
          <div className="bdc-photo-area bdc-photo-fade">
            <img src={actionUrl} alt={name} style={{ objectPosition: photoPosition }} />
          </div>
          {/* Numbering */}
          <div className="bdc-numbering">{numbering}</div>
          {/* Autograph */}
          {autoImage ? (
            <img className="bdc-auto-img" src={autoImage} alt="Autograph" />
          ) : (
            <svg className="bdc-auto-svg" viewBox="0 0 200 60" xmlns="http://www.w3.org/2000/svg">
              <path d={autoPath} fill="none" stroke={autoColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <div className="bdc-bottom-bar">
            <div className="bdc-names-row">
              <div className="bdc-names-outline" style={{ background: nameplateColor || teamColor }} />
              <div className="bdc-names-fill" />
              <span className="bdc-player-name" style={{ fontSize: `${fontSize}px` }}>{name}</span>
            </div>
            <div className="bdc-description"><em>{teamFull}</em></div>
          </div>
          <img className="bdc-team-logo" src={teamLogoUrl} alt={team} onError={(e) => e.target.style.display='none'} />
          <div className="bdc-bd-logo">
            <div className="bdc-bd-bar" />
            <div className="bdc-bd-tick" />
            BD
          </div>
        </div>

        {/* BACK */}
        {(interactive || showBack) && (
        <div className="bdc-card-face bdc-card-back">
          <div className="bdc-back-header" style={{ borderBottomColor: nameplateColor || teamColor }}>
            <img className="bdc-back-headshot" src={headshotUrl} alt={name} style={{ borderColor: nameplateColor || teamColor }} onError={(e) => e.target.style.display='none'} />
            <div className="bdc-back-header-info">
              <div className="bdc-back-player-name">{name}</div>
              <div className="bdc-back-player-meta">{teamFull} · {position} · #{number}</div>
            </div>
            <img className="bdc-back-team-logo" src={teamLogoUrl} alt={team} onError={(e) => e.target.style.display='none'} />
          </div>

          <div className="bdc-bio-row">
            {bio.born && <div className="bdc-bio-item"><div className="bdc-bio-label">Born</div><div className="bdc-bio-value">{bio.born}</div></div>}
            {bio.height && <div className="bdc-bio-item"><div className="bdc-bio-label">Height</div><div className="bdc-bio-value">{bio.height}</div></div>}
            {bio.weight && <div className="bdc-bio-item"><div className="bdc-bio-label">Weight</div><div className="bdc-bio-value">{bio.weight}</div></div>}
            {bio.batsThrows && <div className="bdc-bio-item"><div className="bdc-bio-label">Bats/Throws</div><div className="bdc-bio-value">{bio.batsThrows}</div></div>}
          </div>

          <div className="bdc-stats-title" style={{ color: nameplateColor || teamColor }}>Season Stats</div>
          <table className="bdc-stats-table">
            <thead><tr>{statHeaders.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
            <tbody>
              {stats.map((row, i) => (
                <tr key={i} className={i === stats.length - 1 ? 'highlight' : ''}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>

          {highlights.length > 0 && (
            <>
              <div className="bdc-stats-title" style={{ color: nameplateColor || teamColor }}>Career Highlights</div>
              <table className="bdc-stats-table">
                <thead><tr><th style={{ textAlign: 'left' }}>Achievement</th><th>Year</th></tr></thead>
                <tbody>
                  {highlights.map((h, i) => (
                    <tr key={i}><td>{h.achievement}</td><td>{h.year}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div className="bdc-back-footer">
            <div className="bdc-card-number">{cardNumber}</div>
            <div className="bdc-rarity-tag" style={{ background: '#2563eb' }}>Rare Auto</div>
          </div>
        </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default React.memo(RareAutoCard);
